import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, TrendingUp, Plus, Ruler, Weight, Calendar } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Textarea } from './ui/textarea';
import axios from 'axios';
import { toast } from 'sonner';
import { useViewOnly } from '../contexts/ViewOnlyContext';
import WHOGrowthCharts from './WHOGrowthCharts';
import { getChartPoints } from '../utils/growthChartUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GrowthTracker = ({ baby }) => {
  const navigate = useNavigate();
  // Always read-only for baby/parent users - only doctors can edit
  const viewOnlyMode = true;
  const [growthRecords, setGrowthRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [chartPoints, setChartPoints] = useState(null);
  const [formData, setFormData] = useState({
    date_recorded: '',
    height: '',
    weight: '',
    head_circumference: '',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);

  // Helper function to generate SVG overlay markup for popup windows
  // Now shows only the LATEST data point
  const generateOverlaySVG = (points, width, height, color, rotate) => {
    if (!points || points.length === 0) return '';
    
    // Get only the latest point
    const latestPoint = points[points.length - 1];
    
    return `
      <div style="position: absolute; inset: 0; pointer-events: none; display: flex; align-items: center; justify-content: center; z-index: 10;">
        <svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" 
             style="transform: ${rotate ? 'rotate(90deg)' : 'none'};">
          <circle cx="${latestPoint.x}" cy="${latestPoint.y}" r="6" 
                  fill="${color}" stroke="white" 
                  stroke-width="2" opacity="0.9" />
          <circle cx="${latestPoint.x}" cy="${latestPoint.y}" r="10" fill="none" 
                  stroke="${color}" stroke-width="2" opacity="0.5" />
          <text x="${latestPoint.x}" y="${latestPoint.y - 15}" text-anchor="middle" 
                font-size="10" font-weight="bold" fill="${color}" 
                style="text-shadow: 0 0 3px white, 0 0 3px white">LATEST</text>
        </svg>
      </div>
    `;
  };

  // Handle date input with auto-advance
  const handleDateInput = (e) => {
    let value = e.target.value;
    
    // Remove all non-numeric characters except /
    value = value.replace(/[^\d/]/g, '');
    
    // Auto-add slashes and format
    if (value.length === 2 && !value.includes('/')) {
      value = value + '/';
    } else if (value.length === 5 && value.split('/').length === 2) {
      value = value + '/';
    }
    
    // Limit to dd/mm/yyyy format (10 characters)
    if (value.length > 10) {
      value = value.substring(0, 10);
    }
    
    setFormData(prev => ({ ...prev, date_recorded: value }));
  };

  // Handle backspace for natural deletion
  const handleDateKeyDown = (e) => {
    if (e.key === 'Backspace') {
      const input = e.target;
      const value = input.value;
      const cursorPosition = input.selectionStart;
      
      // If cursor is right after a slash, delete the slash and the character before it
      if (cursorPosition > 0 && value[cursorPosition - 1] === '/') {
        e.preventDefault();
        const newValue = value.substring(0, cursorPosition - 2) + value.substring(cursorPosition);
        setFormData(prev => ({ ...prev, date_recorded: newValue }));
        // Set cursor position after the state updates
        setTimeout(() => {
          input.selectionStart = input.selectionEnd = cursorPosition - 2;
        }, 0);
      }
    }
  };

  useEffect(() => {
    fetchGrowthRecords();
  }, [baby]);

  // Calculate chart points when growth records change
  useEffect(() => {
    console.log('Chart points useEffect triggered:', {
      hasRecords: growthRecords.length > 0,
      recordCount: growthRecords.length,
      hasBirthDate: !!baby.date_of_birth,
      birthDate: baby.date_of_birth
    });
    
    if (growthRecords.length > 0 && baby.date_of_birth) {
      console.log('✅ Calculating chart points for:', {
        birthDate: baby.date_of_birth,
        recordsCount: growthRecords.length,
        records: growthRecords
      });
      const points = getChartPoints(growthRecords, baby.date_of_birth);
      console.log('✅ Chart points calculated:', points);
      setChartPoints(points);
    } else {
      console.log('❌ Not calculating chart points - missing data');
    }
  }, [growthRecords, baby.date_of_birth]);

  const fetchGrowthRecords = async () => {
    try {
      const response = await axios.get(`${API}/growth-records/${baby.id}`);
      // Sort by created_at (if available) or date_recorded in descending order (latest first)
      const sortedRecords = response.data.sort((a, b) => {
        if (a.created_at && b.created_at) {
          return new Date(b.created_at) - new Date(a.created_at);
        }
        return new Date(b.date_recorded) - new Date(a.date_recorded);
      });
      setGrowthRecords(sortedRecords);
      console.log('Growth records sorted:', sortedRecords.slice(0, 2).map(r => ({
        height: r.height,
        weight: r.weight,
        date: r.date_recorded,
        created_at: r.created_at
      })));
    } catch (error) {
      console.error('Error fetching growth records:', error);
      toast.error('Failed to load growth records');
    } finally {
      setLoading(false);
    }
  };

  const handleAddRecord = async (e) => {
    e.preventDefault();
    
    if (!formData.date_recorded || !formData.height || !formData.weight) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const recordData = {
        baby_id: baby.id,
        date_recorded: formData.date_recorded,
        height: parseFloat(formData.height),
        weight: parseFloat(formData.weight),
        head_circumference: formData.head_circumference ? parseFloat(formData.head_circumference) : null,
        notes: formData.notes || null
      };
      
      await axios.post(`${API}/growth-records`, recordData);
      toast.success('Growth record added successfully!');
      setShowAddDialog(false);
      setFormData({
        date_recorded: new Date().toISOString().split('T')[0],
        height: '',
        weight: '',
        head_circumference: '',
        notes: ''
      });
      fetchGrowthRecords();
    } catch (error) {
      console.error('Error adding growth record:', error);
      toast.error('Failed to add growth record');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditRecord = async (e) => {
    e.preventDefault();
    
    if (!formData.date_recorded || !formData.height || !formData.weight) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const recordData = {
        date_recorded: formData.date_recorded,
        height: parseFloat(formData.height),
        weight: parseFloat(formData.weight),
        head_circumference: formData.head_circumference ? parseFloat(formData.head_circumference) : null,
        notes: formData.notes || null
      };
      
      await axios.put(`${API}/growth-records/${editingRecord.id}`, recordData);
      toast.success('Growth record updated successfully!');
      setShowEditDialog(false);
      setEditingRecord(null);
      setFormData({
        date_recorded: new Date().toISOString().split('T')[0],
        height: '',
        weight: '',
        head_circumference: '',
        notes: ''
      });
      fetchGrowthRecords();
    } catch (error) {
      console.error('Error updating growth record:', error);
      toast.error('Failed to update growth record');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteRecord = async (recordId) => {
    if (!window.confirm('Are you sure you want to delete this growth record?')) {
      return;
    }

    try {
      await axios.delete(`${API}/growth-records/${recordId}`);
      toast.success('Growth record deleted successfully!');
      fetchGrowthRecords();
    } catch (error) {
      console.error('Error deleting growth record:', error);
      toast.error('Failed to delete growth record');
    }
  };

  const startEditRecord = (record) => {
    setEditingRecord(record);
    setFormData({
      date_recorded: record.date_recorded,
      height: record.height.toString(),
      weight: record.weight.toString(),
      head_circumference: record.head_circumference ? record.head_circumference.toString() : '',
      notes: record.notes || ''
    });
    setShowEditDialog(true);
  };

  const formatDate = (dateString) => {
    // If already in DD/MM/YYYY format, return as is
    if (dateString && dateString.includes('/')) {
      return dateString;
    }
    // Otherwise convert from ISO format
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const calculateAge = (recordDate) => {
    if (!baby?.date_of_birth || !recordDate) {
      return '';
    }
    
    const birthDate = new Date(baby.date_of_birth);
    const recordDateTime = new Date(recordDate);
    
    if (isNaN(birthDate.getTime()) || isNaN(recordDateTime.getTime())) {
      return '';
    }
    
    const diffTime = Math.abs(recordDateTime - birthDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 30) {
      return `${diffDays} days`;
    } else if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      const remainingDays = diffDays % 30;
      return `${months}m ${remainingDays}d`;
    } else {
      const years = Math.floor(diffDays / 365);
      const remainingMonths = Math.floor((diffDays % 365) / 30);
      return `${years}y ${remainingMonths}m`;
    }
  };

  const getGrowthTrend = (currentValue, previousValue) => {
    if (!previousValue) return null;
    const change = currentValue - previousValue;
    const percentage = ((change / previousValue) * 100).toFixed(1);
    return { change: change.toFixed(1), percentage };
  };

  const getLatestMeasurements = () => {
    if (growthRecords.length === 0) return null;
    const latest = growthRecords[0];
    const previous = growthRecords[1];
    
    return {
      current: latest,
      heightTrend: previous ? getGrowthTrend(latest.height, previous.height) : null,
      weightTrend: previous ? getGrowthTrend(latest.weight, previous.weight) : null
    };
  };

  const measurements = getLatestMeasurements();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading growth records...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="dashboard-header">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/')}
              className="text-white hover:bg-white hover:bg-opacity-20"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold" style={{fontFamily: 'Space Grotesk'}}>Growth Tracker</h1>
                <p className="text-blue-200 text-sm">{baby.name}</p>
              </div>
            </div>
          </div>
          
          {!viewOnlyMode && (
            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild>
                <Button 
                  className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white"
                  data-testid="add-record-button"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Record
                </Button>
              </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Add Growth Record</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddRecord} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="date_recorded">Date *</Label>
                  <Input
                    id="date_recorded"
                    type="text"
                    placeholder="DD/MM/YYYY"
                    value={formData.date_recorded}
                    onChange={handleDateInput}
                    onKeyDown={handleDateKeyDown}
                    maxLength={10}
                    data-testid="record-date-input"
                    required
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="height">Height (cm) *</Label>
                    <Input
                      id="height"
                      type="number"
                      placeholder="50.0"
                      value={formData.height}
                      onChange={(e) => setFormData(prev => ({ ...prev, height: e.target.value }))}
                      min="30"
                      max="200"
                      step="0.1"
                      data-testid="record-height-input"
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="weight">Weight (kg) *</Label>
                    <Input
                      id="weight"
                      type="number"
                      placeholder="3.5"
                      value={formData.weight}
                      onChange={(e) => setFormData(prev => ({ ...prev, weight: e.target.value }))}
                      min="1"
                      max="50"
                      step="0.01"
                      data-testid="record-weight-input"
                      required
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="head_circumference">Head Circumference (cm)</Label>
                  <Input
                    id="head_circumference"
                    type="number"
                    placeholder="35.0"
                    value={formData.head_circumference}
                    onChange={(e) => setFormData(prev => ({ ...prev, head_circumference: e.target.value }))}
                    min="20"
                    max="60"
                    step="0.1"
                    data-testid="record-head-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    placeholder="Any additional notes about this measurement..."
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    rows={3}
                    data-testid="record-notes-input"
                  />
                </div>
                
                <div className="flex space-x-2 pt-4">
                  <Button 
                    type="submit" 
                    className="flex-1"
                    disabled={submitting}
                    data-testid="save-record-button"
                  >
                    {submitting ? 'Saving...' : 'Save Record'}
                  </Button>
                  <Button 
                    type="button"
                    variant="outline" 
                    onClick={() => setShowAddDialog(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          )}

          {/* Edit Growth Record Dialog */}
          {!viewOnlyMode && (
          <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Edit Growth Record</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleEditRecord} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="edit_date_recorded">Date *</Label>
                  <Input
                    id="edit_date_recorded"
                    type="text"
                    placeholder="DD/MM/YYYY"
                    value={formData.date_recorded}
                    onChange={handleDateInput}
                    onKeyDown={handleDateKeyDown}
                    maxLength={10}
                    data-testid="edit-record-date-input"
                    required
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit_height">Height (cm) *</Label>
                    <Input
                      id="edit_height"
                      type="number"
                      placeholder="50.0"
                      value={formData.height}
                      onChange={(e) => setFormData(prev => ({ ...prev, height: e.target.value }))}
                      min="30"
                      max="200"
                      step="0.1"
                      data-testid="edit-record-height-input"
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="edit_weight">Weight (kg) *</Label>
                    <Input
                      id="edit_weight"
                      type="number"
                      placeholder="3.5"
                      value={formData.weight}
                      onChange={(e) => setFormData(prev => ({ ...prev, weight: e.target.value }))}
                      min="1"
                      max="50"
                      step="0.01"
                      data-testid="edit-record-weight-input"
                      required
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="edit_head_circumference">Head Circumference (cm)</Label>
                  <Input
                    id="edit_head_circumference"
                    type="number"
                    placeholder="35.0"
                    value={formData.head_circumference}
                    onChange={(e) => setFormData(prev => ({ ...prev, head_circumference: e.target.value }))}
                    min="20"
                    max="60"
                    step="0.1"
                    data-testid="edit-record-head-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="edit_notes">Notes</Label>
                  <Textarea
                    id="edit_notes"
                    placeholder="Any additional notes about this measurement..."
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    rows={3}
                    data-testid="edit-record-notes-input"
                  />
                </div>
                
                <div className="flex space-x-2 pt-4">
                  <Button 
                    type="submit" 
                    className="flex-1"
                    disabled={submitting}
                    data-testid="update-record-button"
                  >
                    {submitting ? 'Updating...' : 'Update Record'}
                  </Button>
                  <Button 
                    type="button"
                    variant="outline" 
                    onClick={() => {
                      setShowEditDialog(false);
                      setEditingRecord(null);
                      setFormData({
                        date_recorded: new Date().toISOString().split('T')[0],
                        height: '',
                        weight: '',
                        head_circumference: '',
                        notes: ''
                      });
                    }}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          )}
        </div>
      </div>

      <div className="px-6 py-6">
        {/* Latest Measurements Overview */}
        {measurements && (
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 mb-6" data-testid="latest-measurements">
            <h3 className="text-lg font-semibold text-gray-900 mb-4" style={{fontFamily: 'Space Grotesk'}}>Latest Measurements</h3>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <Ruler className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">{measurements.current.height} cm</div>
                  <div className="text-sm text-gray-600">Height</div>
                  {measurements.heightTrend && (
                    <div className={`text-xs ${parseFloat(measurements.heightTrend.change) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {parseFloat(measurements.heightTrend.change) >= 0 ? '+' : ''}{measurements.heightTrend.change} cm
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <Weight className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">{measurements.current.weight} kg</div>
                  <div className="text-sm text-gray-600">Weight</div>
                  {measurements.weightTrend && (
                    <div className={`text-xs ${parseFloat(measurements.weightTrend.change) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {parseFloat(measurements.weightTrend.change) >= 0 ? '+' : ''}{measurements.weightTrend.change} kg
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Growth Records List */}
        {growthRecords.length === 0 ? (
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 text-center" data-testid="no-records">
            <TrendingUp className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Growth Records</h3>
            <p className="text-gray-600 mb-4">Start tracking {baby.name}'s growth by adding the first measurement.</p>
            <Button onClick={() => setShowAddDialog(true)} data-testid="add-first-record-button">
              <Plus className="w-4 h-4 mr-2" />
              Add First Record
            </Button>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden h-[700px] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-100 flex-shrink-0">
              <h3 className="text-lg font-semibold text-gray-900" style={{fontFamily: 'Space Grotesk'}}>Growth History</h3>
            </div>
            
            <div className="divide-y divide-gray-100 overflow-y-auto flex-1">
              {growthRecords.map((record, index) => (
                <div key={record.id} className="px-6 py-4" data-testid={`growth-record-${record.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${index === 0 ? 'bg-green-100' : 'bg-gray-100'}`}>
                        <Calendar className={`w-5 h-5 ${index === 0 ? 'text-green-600' : 'text-gray-600'}`} />
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <h4 className="font-semibold text-gray-900">{formatDate(record.date_recorded)}</h4>
                          {calculateAge(record.date_recorded) && (
                            <span className="text-sm text-gray-500">{calculateAge(record.date_recorded)}</span>
                          )}
                          {index === 0 && (
                            <span className="text-xs bg-green-500 text-white px-2 py-1 rounded-full font-medium">LATEST</span>
                          )}
                        </div>
                        
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
                          <div>
                            <span className="text-gray-600">Height:</span>
                            <span className="ml-1 font-medium">{record.height} cm</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Weight:</span>
                            <span className="ml-1 font-medium">{record.weight} kg</span>
                          </div>
                          {record.head_circumference && (
                            <div>
                              <span className="text-gray-600">Head:</span>
                              <span className="ml-1 font-medium">{record.head_circumference} cm</span>
                            </div>
                          )}
                        </div>
                        
                        {record.notes && (
                          <div className="mt-2 text-sm text-gray-600">
                            <strong>Notes:</strong> {record.notes}
                          </div>
                        )}
                        
                        {record.updated_by_doctor && (
                          <p className="text-xs text-purple-600 mt-2">
                            Updated by {record.updated_by_doctor}
                            {record.updated_by_hospital && ` - ${record.updated_by_hospital}`}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    {!viewOnlyMode && (
                    <div className="flex space-x-2 ml-4">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => startEditRecord(record)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleDeleteRecord(record.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </Button>
                    </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* WHO Growth Charts Section */}
        <div className="mt-8 bg-white rounded-lg shadow-md p-4 sm:p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">WHO Growth Charts</h2>
          <WHOGrowthCharts 
            growthRecords={growthRecords} 
            baby={baby}
            sex={baby?.sex || 'male'}
          />
        </div>
      </div>
    </div>
  );
};

export default GrowthTracker;